'use client'

import { getAppKit, siweAuthStore } from '@/config/appkit'
import { useAuthenticate, useSigner } from '@account-kit/react'
import { useAppKit, useAppKitState, useDisconnect } from '@reown/appkit/react'
import { useCallback, useEffect, useRef, useState } from 'react'

type SiweStep = 'idle' | 'connecting' | 'signing' | 'verifying'

// Cache the targetPublicKey to avoid repeated calls
let cachedTargetPublicKey: string | null = null

export function useSiweAuth() {
	const [step, setStep] = useState<SiweStep>('idle')
	const [error, setError] = useState<string | null>(null)
	const { authenticate } = useAuthenticate()
	const { disconnect } = useDisconnect()
	const { open: modalOpen } = useAppKitState()
	const stepRef = useRef(step)
	const signer = useSigner()

	// Keep ref in sync with state
	useEffect(() => {
		stepRef.current = step
	}, [step])

	// Initialize AppKit on mount
	useEffect(() => {
		getAppKit()
	}, [])

	// Reset step when modal closes without completing auth
	useEffect(() => {
		if (!modalOpen && stepRef.current === 'connecting') {
			setStep('idle')
			// Clean up the auth store
			siweAuthStore.targetPublicKey = null
			siweAuthStore.onAuthComplete = null
		}
	}, [modalOpen])

	const { open } = useAppKit()

	const connect = useCallback(async () => {
		setError(null)
		setStep('connecting')

		try {
			let targetPublicKey = cachedTargetPublicKey

			if (!targetPublicKey) {
				// Use the signer's inner client if available, otherwise access via inner
				if (signer?.inner) {
					targetPublicKey = await signer.inner.targetPublicKey()
				} else {
					// Fallback: access the iframe directly through Turnkey's stamper
					// The AlchemyAccountProvider has already created the turnkey-iframe
					const { AlchemySignerWebClient } = await import('@account-kit/signer')
					const client = new AlchemySignerWebClient({
						connection: { apiKey: process.env.NEXT_PUBLIC_ALCHEMY_API_KEY ?? '' },
						iframeConfig: { iframeContainerId: 'turnkey-iframe' },
					})
					targetPublicKey = await client.targetPublicKey()
				}
				cachedTargetPublicKey = targetPublicKey
			}

			// Set up the auth store
			siweAuthStore.targetPublicKey = targetPublicKey
			siweAuthStore.onAuthComplete = async (jwt: string) => {
				try {
					setStep('verifying')
					await authenticate({ type: 'custom-jwt', jwt })
					// Disconnect from AppKit after successful auth
					await disconnect()
					setStep('idle')
				} catch (err) {
					setStep('idle')
					setError(err instanceof Error ? err.message : 'Authentication failed')
				}
			}
			await open()
		} catch (err) {
			setStep('idle')
			setError(err instanceof Error ? err.message : 'Failed to connect')
		}
	}, [open, authenticate, disconnect, signer])

	const cleanup = useCallback(async () => {
		siweAuthStore.targetPublicKey = null
		siweAuthStore.jwt = null
		siweAuthStore.onAuthComplete = null
		await disconnect()
	}, [disconnect])

	return {
		connect,
		disconnect: cleanup,
		isPending: step !== 'idle',
		step,
		error,
		clearError: () => setError(null),
	}
}
